import { z } from 'zod';

export const PASSWORD_POLICY_HINT =
  'Use 8–128 characters with uppercase, lowercase, a number, and a special character.';

export const passwordPolicySchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be no more than 128 characters')
  .regex(/[A-Z]/, 'Password must include an uppercase letter')
  .regex(/[a-z]/, 'Password must include a lowercase letter')
  .regex(/[0-9]/, 'Password must include a number')
  .regex(/[^A-Za-z0-9]/, 'Password must include a special character');

export function passwordPolicyError(value: string): string | undefined {
  const result = passwordPolicySchema.safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}

/**
 * A password nobody will ever type, for a user created while sign-in is SSO only.
 *
 * `POST /management/create` still REQUIRES `password` (`UserCreate.required`), but with SSO only there
 * is no password form to use it — so the screen sends 32 cryptographically random characters, one of
 * each class the policy demands, and never shows them. Nobody knows it, so it cannot be used.
 */
export function generateUnusablePassword(): string {
  const classes = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%^&*-_=+?',
  ];
  const all = classes.join('');
  const pick = (alphabet: string): string => {
    const [random] = crypto.getRandomValues(new Uint32Array(1));
    return alphabet[(random ?? 0) % alphabet.length] ?? 'x';
  };
  const characters = [...classes.map(pick), ...Array.from({ length: 28 }, () => pick(all))];
  // Shuffle so the four guaranteed classes are not always the first four characters.
  for (let index = characters.length - 1; index > 0; index -= 1) {
    const [random] = crypto.getRandomValues(new Uint32Array(1));
    const swap = (random ?? 0) % (index + 1);
    [characters[index], characters[swap]] = [characters[swap] ?? '', characters[index] ?? ''];
  }
  return characters.join('');
}
